import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from '@compat/router';
import { useQuery } from '@tanstack/react-query';
import { productListByIdsQueryOptions } from '@queries/index';
import BuyButton from '@components/BuyButton';
import LoadingSkeleton from '@components/LoadingSkeleton';
import { alertCircleOrange } from '@images';
import { Picture } from '@ui';
import { observer } from 'mobx-react';
import icons from '@icons';
import './TaskModalProducts.scss';
import { useStores } from '@root/src/stores';

const {
  allDone: { black: allDoneBlackOutlined }
} = icons.Outline;

interface TaskModalProductsProps {
  status: string;
  list: number[];
}

interface Item {
  id: string;
  name: string;
  link_url: string;
  image_url: string;
  available: boolean;
  price: number;
}

const TaskModalProducts: FC<TaskModalProductsProps> = observer(
  ({ list, status = 'access' }) => {
    const { cartStore, xStore } = useStores();
    const { productsAmount, cart } = cartStore;

    const [disabledButtonWasClicked, setDisabledButtonWasClicked] =
      useState(false);

    // Компонент — observer: чтение xStoreId в рендере меняет ключ query
    // при смене магазина (раньше перезапрос делала mobx-reaction)
    const { data: catalogItems, isPending: loading } = useQuery(
      productListByIdsQueryOptions(list, xStore?.xStoreId)
    );

    const data = useMemo<Item[]>(
      () =>
        (catalogItems ?? []).map(item => ({
          id: item.id,
          name: item.title || '',
          link_url: `/product/${item.id}`,
          image_url: item?.fileItem?.cacheItem?.[0]?.filePath || '',
          available: !!item.available,
          price: Number(
            item.priceBlockWeightPrice
              ? item.currentPriceField
              : item.currentPriceField2
          )
        })),
      [catalogItems]
    );

    const onDisabledButtonClick = useCallback(() => {
      setDisabledButtonWasClicked(true);
    }, []);

    const ready =
      !loading &&
      status === 'progress' &&
      data.map(a => productsAmount[a.id] > 0).filter(a => !a).length === 0;
    if (loading && status !== 'complete') {
      return (
        <LoadingSkeleton
          style={{
            width: '100%',
            height: '48px',
            borderRadius: '8px'
          }}
        />
      );
    }
    if (!data || data.length === 0) {
      return null;
    }
    return (
      <>
        <div className='task-modal-products'>
          <p className='task-modal-products__tip'>Добавьте товары в корзину</p>
          <ul className='task-modal-products__list custom-scroll'>
            {data.map(item => (
              <li
                className={'task-modal-products__list-item'}
                key={`task-product-${item.id}`}
              >
                <Link
                  className='task-modal-products__list-item__product-link'
                  to={`${item.link_url}`}
                >
                  <div className='task-modal-products__list-item__product-img-wrapper'>
                    <img
                      className='task-modal-products__list-item__product-img'
                      src={item.image_url}
                      alt={item.name}
                    />
                  </div>
                  <p className='task-modal-products__list-item__product-name'>
                    {item.name}
                  </p>
                </Link>
                <div className='task-modal-products__list-item__product-wrapper'>
                  <div className='task-modal-products__list-item__product-pricesWrapper'>
                    <p className='task-modal-products__product-price'>
                      {productsAmount[item.id] > 0
                        ? Number(item.price) * productsAmount[item.id]
                        : Number(item.price)}{' '}
                      ₽
                    </p>
                    {productsAmount[item.id] > 0 && (
                      <p className='task-modal-products__product-price-total'>
                        <span>{productsAmount[item.id]}</span> х{' '}
                        <span>{Number(item.price)}</span> ₽
                      </p>
                    )}
                  </div>
                  <BuyButton
                    canBeBought={item.available}
                    disabled={!(status === 'progress')}
                    className='buy-btn task-modal-products__buy-btn'
                    productId={Number(item.id)}
                    available={item.available}
                    onDisabledButtonClick={onDisabledButtonClick}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
        {status !== 'progress' && disabledButtonWasClicked && (
          <div className='task-modal-products__help '>
            <Picture
              className='task-modal-products__help-icon'
              icon={alertCircleOrange}
              height='24'
            />
            <p className='task-modal-products__help-text danger'>
              Сначала активируйте задание!
            </p>
          </div>
        )}
        {status === 'progress' && ready && (
          <div className='task-modal-products__help'>
            <Picture
              className='task-modal-products__help-icon'
              icon={allDoneBlackOutlined}
              height='24'
            />
            <p className='task-modal-products__help-text'>
              Отлично, осталось оформить заказ. Убедитесь, что сумма заказа
              больше минимальной – {cart?.minAllowableTotal?.toString()} ₽.
            </p>
          </div>
        )}
      </>
    );
  }
);

export default TaskModalProducts;
